import React from 'react';
import Button from '@mui/material/Button';
import { Box, TextField, Typography, IconButton, Divider } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AreYouSureModal from '../../../components/AreYouSureModal';
import getStudentName from '../../../utils/getStudentName';


function ConsultationButtonModal({
    admin_name,
    event,
    attendance,
    enqueueSnackbar,
    colors,
    handlePopOverClose,
    socketConnected,
    socket,
}) {

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        maxHeight: '80%',
        overflowY: 'auto',
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const [memo, setMemo] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const [areYouSureModalOpen, setAreYouSureModalOpen] = React.useState(false);
    const [consultationToDelete, setConsultationToDelete] = React.useState(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); handlePopOverClose(); };

    // read directly from props — the weekly socket update replaces attendance.student_course,
    // so the list refreshes live after add/delete
    const consultations = attendance.student_course?.student_course_consultations || [];

    async function handleAdd() {
        if (!socketConnected) {
            enqueueSnackbar("Error: Cannot save the changes. Please try again.", { variant: "error" });
            return;
        }

        if (!memo || memo.trim() === "") {
            enqueueSnackbar("Please write a memo for the consultation.", { variant: "warning" });
            return;
        }

        try {
            // emit to server
            socket.emit("post.StudentCourseConsultation", {
                student_course_id: attendance.student_course.id,
                memo: memo.trim(),
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Error: Cannot save the changes. Please try again.", { variant: "error" });
            return;
        }

        setMemo("");
    };

    async function handleDelete() {
        if (!socketConnected) {
            enqueueSnackbar("Error: Cannot delete the consultation. Please try again.", { variant: "error" });
            return;
        }

        if (!consultationToDelete) {
            return;
        }

        try {
            // emit to server
            socket.emit("delete.StudentCourseConsultation", {
                consultation_id: consultationToDelete.id,
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Error: Cannot delete the consultation. Please try again.", { variant: "error" });
            return;
        }

        setConsultationToDelete(null);
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                sx={{
                    p: 1,
                    width: "100%",
                    bgcolor: colors.purpleAccent[700],
                    borderRadius: "0px",
                    '&:hover': {
                        bgcolor: colors.purpleAccent[800],
                    },
                }}
            >
                Consultations{consultations.length > 0 ? ` (${consultations.length})` : ""}
            </Button>
            <Modal
                aria-labelledby="consultation-modal"
                aria-describedby="consultation-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h4" color={colors.primary[450]}>
                            Consultations for {attendance.student ? getStudentName(attendance.student) : "Unknown"}
                        </Typography>
                        <CloseIcon sx={{ position: "absolute", right: 10, top: 10, cursor: "pointer" }} onClick={handleClose} />
                        <Box sx={{ display: "flex", flexDirection: "column", mt: 2, gap: 1 }}>
                            {consultations.length > 0 ? (
                                consultations.map((consultation) => (
                                    <Box
                                        key={consultation.id}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "flex-start",
                                            gap: 1,
                                            p: 1,
                                            border: `1px solid ${colors.grey[600]}`,
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <SupportAgentIcon fontSize="small" sx={{ mt: "2px", color: colors.purpleAccent[500] }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                                                {consultation.memo}
                                            </Typography>
                                            <Typography variant="caption" color={colors.grey[400]}>
                                                {consultation.created_by ? `by ${consultation.created_by}` : ""}
                                                {consultation.createdAt ? ` on ${new Date(consultation.createdAt).toLocaleDateString()}` : ""}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setConsultationToDelete(consultation);
                                                setAreYouSureModalOpen(true);
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Box>
                                ))
                            ) : (
                                <Typography color={colors.grey[400]}>
                                    No consultations yet.
                                </Typography>
                            )}
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <TextField
                                label="New consultation memo (add date or time in the memo if needed)"
                                multiline
                                rows={4}
                                variant="outlined"
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                sx={{ width: 600, mt: 1 }}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                color="secondary"
                                sx={{ width: 200, mt: 2 }}
                                onClick={() => handleAdd()}
                            >
                                Add Consultation
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ width: 200, mt: 2 }}
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                        </Box>
                        <AreYouSureModal
                            colors={colors}
                            open={areYouSureModalOpen}
                            setOpen={setAreYouSureModalOpen}
                            title="Delete a consultation"
                            message="Please confirm to delete this consultation. This action cannot be undone."
                            onConfirm={handleDelete}
                        />
                    </Box>
                </Fade>
            </Modal>
        </>
    )
}

export default ConsultationButtonModal;
